/**
 * Iberohemp — Blog product gallery
 * Renders a dynamic gallery of top-priced products at the end of blog
 * articles, matched to the article's category (flowers / hash / oils)
 * across ES/EN/FR/IT/DE/PT-PT locales.
 *
 * Toggle: set window.IBEROHEMP_GALLERY_ENABLED = false to disable.
 */
window.IBEROHEMP_GALLERY_ENABLED = true;

(function () {
  "use strict";

  if (window.IBEROHEMP_GALLERY_ENABLED === false) return;

  const CONFIG = {
    collectionsByCategory: {
      flores: "flores-cbd-premium",
      hachis: "hachis",
      aceites: "aceites-de-cbd",
    },
    defaultCategory: "flores",
    productsPerGallery: 4,
    currencyLocaleFallback: "es-ES",
    excludeSingleVariantProducts: true,
  };

  const KEYWORDS = {
    hachis: [
      "hachis", "hachís", "chocolate", "polen",
      "hash", "hashish",
      "haschisch", "hasch",
      "fumo nero",
      "haxixe",
    ],
    aceites: [
      "aceite", "aceites", "oleo",
      "oil", "oils",
      "huile", "huiles",
      "olio", "oli",
      "ol", "oel", "hanfol",
      "óleo", "azeite",
    ],
    flores: [
      "flor", "flores", "cogollo", "cogollos", "cannabis",
      "flower", "flowers", "bud", "buds", "weed",
      "fleur", "fleurs",
      "fiore", "fiori",
      "blute", "bluete", "blume", "blumen",
    ],
  };

  function normalize(str) {
    return (str || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  /**
   * Scores article title/tags/blog handle against the keyword dictionary
   * and returns the best-matching category, falling back to the default.
   */
  function detectCategory(rawTitle, rawTags, rawBlogHandle) {
    const haystack = normalize(
      [rawTitle, rawTags, rawBlogHandle].filter(Boolean).join(" ")
    );

    const scores = { flores: 0, hachis: 0, aceites: 0 };

    Object.keys(KEYWORDS).forEach((cat) => {
      KEYWORDS[cat].forEach((kw) => {
        const kwNorm = normalize(kw);
        if (!kwNorm) return;
        const escaped = kwNorm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`\\b${escaped}\\b`, "g");
        const matches = haystack.match(re);
        if (matches) scores[cat] += matches.length;
      });
    });

    const best = Object.keys(scores).reduce((a, b) =>
      scores[b] > scores[a] ? b : a
    );

    return scores[best] > 0 ? best : CONFIG.defaultCategory;
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || CONFIG.currencyLocaleFallback, {
        style: "currency",
        currency: currency || "EUR",
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + " €";
    }
  }

  /**
   * Builds the locale path prefix used by Shopify Markets / Translate &
   * Adapt to serve localized storefront content (e.g. "/en", "/fr").
   * Spanish is assumed to be the root locale with no prefix.
   */
  function getLocalePrefix() {
    const htmlLang = (document.documentElement.lang || "").toLowerCase();
    const shopifyLocale = (window.Shopify && window.Shopify.locale || "").toLowerCase();
    const lang = (shopifyLocale || htmlLang).split("-")[0];

    if (!lang || lang === "es") return "";
    return "/" + lang;
  }

  /**
   * Resolves a product's smallest-quantity variant.
   * Priority: weight parsed from variant title (explicit, e.g. "10g")
   * -> variant.grams (Shopify shipping weight, unreliable if unset)
   * -> lowest-priced variant as last resort.
   */
  function getSmallestQuantityVariant(product) {
    const variants = product.variants || [];
    if (!variants.length) return null;

    const withParsedTitle = variants
      .map((v) => {
        const match = (v.title || "").match(/(\d+(?:[.,]\d+)?)\s*g\b/i);
        return match ? { v, qty: parseFloat(match[1].replace(",", ".")) } : null;
      })
      .filter(Boolean);

    if (withParsedTitle.length) {
      withParsedTitle.sort((a, b) => a.qty - b.qty);
      return withParsedTitle[0].v;
    }

    const withGrams = variants
      .map((v) => ({ v, grams: v.grams || 0 }))
      .filter((x) => x.grams > 0);

    if (withGrams.length) {
      withGrams.sort((a, b) => a.grams - b.grams);
      return withGrams[0].v;
    }

    return variants.reduce((cheapest, v) =>
      (parseFloat(v.price) || 0) < (parseFloat(cheapest.price) || 0) ? v : cheapest
    );
  }

  function getSmallestQuantityPrice(product) {
    const variant = getSmallestQuantityVariant(product);
    return variant ? parseFloat(variant.price) || 0 : 0;
  }

  /**
   * Verifies the locale-prefixed collection endpoint actually resolves
   * before committing to it. Not every language/market on the storefront
   * necessarily uses a URL subfolder (e.g. some markets translate content
   * without changing the path), so a naive prefix can 404 and silently
   * empty out the gallery. Falls back to the unprefixed (root) endpoint
   * whenever the prefixed one doesn't return usable data.
   */
  async function resolveWorkingLocalePrefix(collectionHandle) {
    const prefix = getLocalePrefix();
    if (!prefix) return "";

    try {
      const res = await fetch(
        `${prefix}/collections/${collectionHandle}/products.json?limit=1`,
        { credentials: "same-origin" }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.products && data.products.length) return prefix;
      }
    } catch (e) {
      // falls through to root prefix below
    }

    return "";
  }

  /**
   * Fetches all products in a collection via the public .json endpoint
   * (locale-aware, so translated titles come through when the storefront
   * is browsed in EN/FR/IT/DE/PT), ranks them by smallest-quantity-variant
   * price (descending), and returns the top N. Single-variant products
   * (e.g. bulk/trim products sold only in one large format) are excluded
   * so they don't get mixed in with the per-gram catalog.
   */
  async function fetchTopPricedProducts(collectionHandle, limit) {
    const perPage = 250;
    let page = 1;
    let all = [];
    const localePrefix = await resolveWorkingLocalePrefix(collectionHandle);

    while (true) {
      const url = `${localePrefix}/collections/${collectionHandle}/products.json?limit=${perPage}&page=${page}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) break;
      const data = await res.json();
      const products = data.products || [];
      all = all.concat(products);
      if (products.length < perPage) break;
      page += 1;
      if (page > 10) break;
    }

    if (CONFIG.excludeSingleVariantProducts) {
      all = all.filter((p) => (p.variants || []).length > 1);
    }

    const ranked = all.map((p) => ({
      product: p,
      smallestQtyPrice: getSmallestQuantityPrice(p),
    }));

    ranked.sort((a, b) => b.smallestQtyPrice - a.smallestQtyPrice);

    return ranked.slice(0, limit).map((x) => x.product);
  }

  function renderGallery(container, products) {
    if (!products.length) {
      container.remove();
      return;
    }

    const style = `
      <style>
        .ih-gallery {
          margin-top: 2rem !important;
        }
        @media (max-width: 768px) {
          .ih-gallery { margin-top: 2.5rem !important; padding-top: 1.5rem; text-align:center;}
        }
        .ih-gallery__title {
          font-size: 2rem;
          font-weight: 800;
          letter-spacing: -0.01em;
          margin-bottom: 0 !important;
          color:black;
          display: inline-block;
        }
        .ih-gallery__grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }
        @media (max-width: 900px) {
          .ih-gallery__grid { grid-template-columns: repeat(3, 1fr); } 
        }
        @media (max-width: 560px) {
          .ih-gallery__grid { grid-template-columns: repeat(2, 1fr); gap: 1.1rem; }
          .ih-gallery__title {margin-bottom:10px !important}
        }
        .ih-gallery__card {
          position: relative;
          display: block;
          text-decoration: none !important;
          color: inherit;
          background: transparent;
          text-align:left !important
        }
        .ih-gallery__badge {
          position: absolute;
          top: 10px;
          left: 10px;
          z-index: 2;
          background: rgb(248, 58, 58);
          color: #fff;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
        }
        .ih-gallery__img-wrap {
          position: relative;
          width: 100% !important;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: #f7f7f7;
          border-left: 4px solid rgb(41, 169, 118) !important;
          border-radius: 12px 0 0 12px;
          margin: 0 !important;
          padding: 0 !important;
        }
        .ih-gallery__img-wrap img {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          object-fit: cover !important;
          object-position: center !important;
          display: block !important;
          margin: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          transition: transform .25s ease;
        }
        .ih-gallery__card:hover .ih-gallery__img-wrap img {
          transform: scale(1.05);
        }
        .ih-gallery__info {
          padding: 0 !important;
        }
        .ih-gallery__name {
          font-size: 1.01rem;
          font-weight: 700;
          color: rgb(41, 169, 118);
          margin: 0;
          line-height: 1.25;
        }
        .ih-gallery__prices {
          display: flex;
          align-items: baseline;
          gap: 0.4rem;
          margin: 10px 0 0 !important;
        }
        .ih-gallery__price {
          font-size: 0.92rem;
          font-weight: 500;
          color: rgb(248, 58, 58);
        }
        .ih-gallery__price-compare {
          font-size: 0.78rem;
          font-weight: 500;
          color: #9a9a9a;
          text-decoration: line-through;
        }
      </style>
    `;

    const cards = products
      .map((p) => {
        const img = p.images && p.images[0] ? p.images[0].src : "";
        const variant = getSmallestQuantityVariant(p);
        const price = variant ? parseFloat(variant.price) || 0 : 0;
        const compareAt = variant ? parseFloat(variant.compare_at_price) || 0 : 0;
        const onSale = compareAt > price;

        return `
          <a class="ih-gallery__card" href="/products/${p.handle}">
            ${onSale ? '<span class="ih-gallery__badge">Oferta</span>' : ""}
            <div class="ih-gallery__img-wrap">
              ${img ? `<img src="${img}" loading="lazy" alt="${p.title}">` : ""}
            </div>
            <div class="ih-gallery__info">
              <p class="ih-gallery__name">${p.title}</p>
              <p class="ih-gallery__prices">
                <span class="ih-gallery__price">${formatMoney(price * 100, "EUR")}</span>
                ${onSale ? `<span class="ih-gallery__price-compare">${formatMoney(compareAt * 100, "EUR")}</span>` : ""}
              </p>
            </div>
          </a>
        `;
      })
      .join("");

    container.innerHTML = `
      ${style}
      <div class="ih-gallery">
        <p class="ih-gallery__title">${container.dataset.galleryTitle || "Productos destacados"}</p>
        <div class="ih-gallery__grid">${cards}</div>
      </div>
    `;
  }

  async function init() {
    const containers = document.querySelectorAll("[data-ih-product-gallery]");
    for (const container of containers) {
      const title = container.dataset.postTitle || "";
      const tags = container.dataset.postTags || "";
      const blogHandle = container.dataset.blogHandle || "";
      const forcedCategory = container.dataset.forceCategory || "";

      const category =
        forcedCategory && CONFIG.collectionsByCategory[forcedCategory]
          ? forcedCategory
          : detectCategory(title, tags, blogHandle);

      const handle = CONFIG.collectionsByCategory[category];
      if (!handle) continue;

      try {
        const products = await fetchTopPricedProducts(handle, CONFIG.productsPerGallery);
        renderGallery(container, products);
      } catch (e) {
        console.error("[ih-gallery] error loading products:", e);
        container.remove();
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();